import { Injectable } from '@angular/core';
import * as CryptoJS from 'crypto-js';
import { environmentConfig } from '../../environments/environment.config';

@Injectable({
  providedIn: 'root',
})
export class StorageEncryptionService {
  private encryptionKey: string;

  constructor() {
    // Leer la clave de encriptación desde archivo .env
    this.encryptionKey =
      environmentConfig.ENCRYPTION_KEY || 'DomoBlock2024@DefaultKey!';

    // Validar que la clave no esté vacía
    if (!this.encryptionKey || this.encryptionKey.length < 16) {
      throw new Error(
        'Clave de encriptación no válida en archivo .env (ENCRYPTION_KEY)',
      );
    }
  }

  /**
   * Establece una nueva llave de encriptación (opcional, si se desea cambiar en tiempo de ejecución)
   * @param key Nueva llave de encriptación (debe tener al menos 16 caracteres)
   */
  setEncryptionKey(key: string): void {
    if (!key || key.length < 16) {
      throw new Error(
        'Clave de encriptación no válida (debe tener al menos 16 caracteres)',
      );
    }
    this.encryptionKey = key;
  }

  /**
   * Encripta datos usando AES
   * @param data - Datos a encriptar
   * @returns String encriptado o cadena vacía si hay error
   */
  private encryptData(data: any): string {
    try {
      const dataString = JSON.stringify(data);
      console.log('Y) Encriptando datos:', data);
      console.log('Y) Usando clave de encriptación:', this.encryptionKey);
      const encrypted = CryptoJS.AES.encrypt(
        dataString,
        this.encryptionKey,
      ).toString();
      console.log('Y) Datos encriptados:', encrypted);
      return encrypted;
    } catch (error) {
      return '';
    }
  }

  /**
   * Desencripta datos usando AES
   * @param encryptedData - Datos encriptados
   * @returns Datos desencriptados o null si hay error
   */
  private decryptData(encryptedData: string): any {
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedData, this.encryptionKey);
      const decryptedData = bytes.toString(CryptoJS.enc.Utf8);
      console.log('Y) Usando clave de desencriptación:', this.encryptionKey);
      console.log('Y) Datos encriptados a desencriptar:', encryptedData);
      console.log('Y) Datos desencriptados:', decryptedData);
      return JSON.parse(decryptedData);
    } catch (error) {
      return null;
    }
  }

  /**
   * Guarda datos encriptados en localStorage
   * @param key - Clave para localStorage
   * @param data - Datos a guardar
   * @returns true si se guardó exitosamente, false en caso contrario
   */
  saveEncryptedData(key: string, data: any): boolean {
    try {
      const encryptedData = this.encryptData(data);
      if (encryptedData) {
        localStorage.setItem(key, encryptedData);
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }

  /**
   * Obtiene y desencripta datos desde localStorage
   * @param key - Clave para localStorage
   * @returns Datos desencriptados o null si no existen o hay error
   */
  getDecryptedData(key: string): any {
    try {
      const encryptedData = localStorage.getItem(key);
      if (encryptedData) {
        const decryptedData = this.decryptData(encryptedData);
        return decryptedData;
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Elimina datos del localStorage
   * @param key - Clave para localStorage
   * @returns true si se eliminó exitosamente, false en caso contrario
   */
  removeData(key: string): boolean {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Verifica si existe una clave en localStorage
   * @param key - Clave para localStorage
   * @returns true si existe, false en caso contrario
   */
  hasData(key: string): boolean {
    try {
      return localStorage.getItem(key) !== null;
    } catch (error) {
      return false;
    }
  }

  /**
   * Limpia todos los datos relacionados con DomoBlock del localStorage
   * Busca todas las claves que contengan 'domoblock'
   */
  clearAllDomoblockData(): void {
    try {
      const keysToRemove: string[] = [];

      // Iterar sobre todas las claves del localStorage
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.toLowerCase().includes('domoblock')) {
          keysToRemove.push(key);
        }
      }

      // Eliminar todas las claves encontradas
      keysToRemove.forEach((key) => {
        localStorage.removeItem(key);
      });
    } catch (error) {}
  }

  /**
   * Obtiene información de uso del localStorage
   * @returns Objeto con información de uso
   */
  getStorageInfo(): {
    totalKeys: number;
    domoblockKeys: number;
    estimatedSize: number;
  } {
    try {
      let totalKeys = localStorage.length;
      let domoblockKeys = 0;
      let estimatedSize = 0;

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
          const value = localStorage.getItem(key);
          if (value) {
            estimatedSize += key.length + value.length;
            if (key.toLowerCase().includes('domoblock')) {
              domoblockKeys++;
            }
          }
        }
      }

      return {
        totalKeys,
        domoblockKeys,
        estimatedSize: estimatedSize * 2, // Aproximación en bytes (UTF-16)
      };
    } catch (error) {
      return { totalKeys: 0, domoblockKeys: 0, estimatedSize: 0 };
    }
  }
}
